
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SidebarInset } from "@/components/ui/sidebar";
import { PortfolioHeader } from "@/components/portfolio/build/PortfolioHeader";
import { SavePortfolioDialog } from "@/components/portfolio/build/SavePortfolioDialog";
import { PortfolioContent } from "@/components/portfolio/build/components/PortfolioContent";
import { usePortfolioData } from "@/components/portfolio/build/hooks/usePortfolioData";
import { usePortfolioActions } from "@/components/portfolio/build/hooks/usePortfolioActions";
import { PortfolioDebugInfo } from "@/components/portfolio/build/PortfolioDebugInfo";
import { PageContainer } from "@/components/ui/PageContainer";
import { Spinner } from "@/components/ui/Spinner";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
// Note: Totals are now computed within PortfolioContent using type-aware calculations

export default function BuildPortfolio() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [isEditingName, setIsEditingName] = useState(false);
  const isMobile = useIsMobile();
  
  const {
    portfolio,
    isLoading,
    portfolioSize,
    portfolioName,
    setPortfolioName,
    handlePortfolioSizeChange,
    updatePortfolioSize,
    refetch: refetchPortfolio,
    error
  } = usePortfolioData(portfolioId);

  // Handle portfolio not found error with redirect
  useEffect(() => {
    if (error?.message === 'PORTFOLIO_NOT_FOUND') {
      toast.error("Portfolio not found. Redirecting to portfolio list...");
      setTimeout(() => {
        navigate('/portfolios');
      }, 2000);
    }
  }, [error, navigate]);

  const {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isSaveDialogOpen,
    setIsSaveDialogOpen,
    handlePortfolioNameChange,
    handleDeletePortfolio,
    handleSavePortfolio,
    handleCreateNewPortfolio
  } = usePortfolioActions(portfolioId, portfolioName, portfolioSize, refetchPortfolio);

  const handleNameSaveAndExit = async () => {
    await handlePortfolioNameChange();
    setIsEditingName(false);
  };

  // Totals are handled inside PortfolioContent; no need to calculate here

  return (
    <div className="flex-1">
      <SidebarInset>
        <PortfolioHeader portfolioName={portfolio?.name} />
        <div className="flex-1 overflow-y-auto">
          {isLoading || !portfolio ? (
            <div className="flex items-center justify-center min-h-[60vh]" aria-busy>
              <Spinner size="md" variant="muted" ariaLabel="Loading portfolio" />
              <span className="ml-2 text-sm text-muted-foreground">Loading portfolio...</span>
            </div>
          ) : (
            <PageContainer maxWidthClassName="max-w-[960px]">
              {/* Debug component to help diagnose allocation issues */}
              <PortfolioDebugInfo portfolio={portfolio} />
              <PortfolioContent
                portfolio={portfolio}
                portfolioId={portfolioId || ''}
                portfolioName={portfolioName}
                portfolioSize={portfolioSize}
                isEditingName={isEditingName}
                setIsEditingName={setIsEditingName}
                onPortfolioNameChange={setPortfolioName}
                onPortfolioSizeChange={handlePortfolioSizeChange}
                onPortfolioSizeUpdate={updatePortfolioSize}
                onNameSave={handleNameSaveAndExit}
                onAssetAdded={refetchPortfolio}
                onDeleteClick={handleDeletePortfolio}
                onSaveClick={handleSavePortfolio}
                isDeleteDialogOpen={isDeleteDialogOpen}
                setIsDeleteDialogOpen={setIsDeleteDialogOpen}
                portfolioType={portfolio?.portfolio_type}
              />
            </PageContainer>
          )}
        </div>

        <SavePortfolioDialog
          isOpen={isSaveDialogOpen}
          onOpenChange={setIsSaveDialogOpen}
          portfolioName={portfolioName}
          onPortfolioNameChange={setPortfolioName}
          onSave={handleCreateNewPortfolio}
        />
      </SidebarInset>
    </div>
  );
}
